
import { useEffect, useState } from "react";
import { BlogList } from "./BlogList";
import { FeaturedPost } from "./FeaturedPost";
import { NoResults } from "./NoResults";
import { BlogPost } from "@/types/blog";

interface BlogResultsProps {
  posts: BlogPost[];
  loading: boolean;
  searchTerm: string;
  selectedCategory: string;
  onResetFilters: () => void;
  selectedCategoryId: number | null;
}

export const BlogResults = ({ 
  posts, 
  loading, 
  searchTerm, 
  selectedCategory,
  onResetFilters,
  selectedCategoryId
}: BlogResultsProps) => {
  // Stato locale per i post filtrati
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  
  // Effetto per filtrare i post in base alla categoria selezionata
  useEffect(() => {
    console.log(`[BlogResults] Filtrando ${posts.length} post per categoria ID: ${selectedCategoryId}, nome: "${selectedCategory}"`);
    
    if (!selectedCategoryId) {
      // Se non c'è categoria selezionata, mostra tutti i post
      setFilteredPosts(posts);
      return;
    }
    
    // Filtra i post per categoria
    const postsInCategory = posts.filter(post => {
      // Verifica se il post appartiene alla categoria selezionata
      return post.categoryIds?.includes(selectedCategoryId);
    });
    
    console.log(`[BlogResults] Trovati ${postsInCategory.length} post nella categoria ${selectedCategoryId}`);
    setFilteredPosts(postsInCategory);
  }, [posts, selectedCategoryId, selectedCategory]);
  
  // Se non ci sono post e non in caricamento, mostra nessun risultato
  // Ma solo se c'è un filtro attivo (categoria o ricerca)
  if (!loading && filteredPosts.length === 0) {
    const isFiltering = !!searchTerm || !!selectedCategory;
    
    if (isFiltering) {
      return <NoResults onResetFilters={onResetFilters} />;
    }
    
    // Se non ci sono filtri attivi e non ci sono post, mostra un messaggio generico
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <h3 className="text-xl font-semibold mb-2">Caricamento articoli in corso</h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          Attendere mentre carichiamo gli articoli del blog...
        </p>
      </div>
    );
  }

  const isFiltering = !!searchTerm || !!selectedCategory;

  // Quando si filtra, tratta tutti i post come post normali (no featured)
  // Quando non si filtra, usa il primo post come featured (se presente)
  const featuredPost = !isFiltering && filteredPosts.length > 0 ? filteredPosts[0] : null;
  const regularPosts = isFiltering 
    ? filteredPosts 
    : (filteredPosts.length > 0 ? (featuredPost ? filteredPosts.slice(1) : filteredPosts) : []);

  return (
    <>
      {!loading && featuredPost && !isFiltering && (
        <div className="mb-12">
          <FeaturedPost post={featuredPost} />
        </div>
      )}
      
      {regularPosts.length > 0 && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold mb-6">
            {isFiltering
              ? `Risultati${selectedCategory ? ` per "${selectedCategory}"` : ""}${searchTerm ? ` con "${searchTerm}"` : ""}`
              : "Articoli Recenti"}
          </h2>
          
          <BlogList posts={regularPosts} />
        </div>
      )}
    </>
  );
};
